import { useState } from 'react';
import { cn } from '@/lib/utils';
import { api, BuilderSession } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, ChevronUp, Settings, DollarSign, Eye, Palette, Loader2 } from 'lucide-react';

interface ConfigPanelProps {
  session: BuilderSession | null;
  onSettingsChange: (settings: unknown) => void;
  /** 'collapsible' renders the full collapsible panel; 'dialog' renders just the cards grid */
  variant?: 'collapsible' | 'dialog';
  className?: string;
}

interface ArcheSettings {
  name: string;
  pricing: {
    model: 'free' | 'fixed' | 'per_use';
    priceCents?: number;
  };
  visibility: {
    isListed: boolean;
    isPublicAccess: boolean;
    isFullScreen: boolean;
  };
  theme: {
    mode: 'light' | 'dark' | 'auto';
  };
}

export function ConfigPanel({
  session,
  onSettingsChange,
  variant = 'collapsible',
  className,
}: ConfigPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const currentSettings = (session?.currentSettings || {}) as Partial<ArcheSettings>;

  // Separate local state for name input (only saves on blur/Enter)
  const [localName, setLocalName] = useState(session?.currentName || currentSettings.name || '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);

  const [settings, setSettings] = useState<ArcheSettings>({
    name: session?.currentName || currentSettings.name || '',
    pricing: currentSettings.pricing || { model: 'free' },
    visibility: currentSettings.visibility || {
      isListed: true,
      isPublicAccess: false,
      isFullScreen: false,
    },
    theme: currentSettings.theme || { mode: 'auto' },
  });

  const updateSettings = (updates: Partial<ArcheSettings>) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  const saveName = async () => {
    const trimmedName = localName.trim();

    if (!trimmedName) {
      setNameError('Name is required');
      return;
    }

    if (trimmedName === settings.name) {
      return;
    }

    setIsCheckingName(true);
    setNameError(null);

    try {
      const result = await api.checkArcheName(trimmedName, session?.archId || undefined);
      if (!result.available) {
        setNameError('This name is already taken');
        return;
      }
      updateSettings({ name: trimmedName });
    } catch {
      setNameError('Failed to check name availability');
    } finally {
      setIsCheckingName(false);
    }
  };

  const cardsGrid = (
    <div
      className={cn(
        'grid grid-cols-1 md:grid-cols-2 gap-4',
        variant === 'collapsible' ? 'p-4 lg:grid-cols-4' : '',
        className,
      )}
    >
      {/* Name */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="arche-name">Arche Name</Label>
            <div className="relative">
              <Input
                id="arche-name"
                type="text"
                value={localName}
                onChange={(e) => {
                  setLocalName(e.target.value);
                  setNameError(null);
                }}
                onBlur={saveName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    saveName();
                    e.currentTarget.blur();
                  }
                }}
                placeholder="My Custom Arche"
                className={nameError ? 'border-destructive pr-8' : ''}
                disabled={isCheckingName}
              />
              {isCheckingName && (
                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            {nameError && <p className="text-sm text-destructive">{nameError}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Pricing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={settings.pricing.model}
            onValueChange={(value: 'free' | 'fixed' | 'per_use') =>
              updateSettings({
                pricing: { ...settings.pricing, model: value },
              })
            }
          >
            <SelectTrigger id="pricing-model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="fixed">Fixed Price</SelectItem>
              <SelectItem value="per_use">Per Use</SelectItem>
            </SelectContent>
          </Select>

          {settings.pricing.model !== 'free' && (
            <div className="space-y-2">
              <Label htmlFor="price">Price (cents)</Label>
              <Input
                id="price"
                type="number"
                min="0"
                value={settings.pricing.priceCents || ''}
                onChange={(e) =>
                  updateSettings({
                    pricing: {
                      ...settings.pricing,
                      priceCents: parseInt(e.target.value) || 0,
                    },
                  })
                }
                placeholder="0"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visibility */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-listed"
              checked={settings.visibility.isListed}
              onCheckedChange={(checked) =>
                updateSettings({
                  visibility: {
                    ...settings.visibility,
                    isListed: checked as boolean,
                  },
                })
              }
            />
            <Label htmlFor="is-listed" className="text-sm font-normal">
              Listed (visible in the Arche Store)
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-public-access"
              checked={settings.visibility.isPublicAccess}
              onCheckedChange={(checked) =>
                updateSettings({
                  visibility: {
                    ...settings.visibility,
                    isPublicAccess: checked as boolean,
                  },
                })
              }
            />
            <Label htmlFor="is-public-access" className="text-sm font-normal">
              Public Access (viewable when not logged in)
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-fullscreen"
              checked={settings.visibility.isFullScreen}
              onCheckedChange={(checked) =>
                updateSettings({
                  visibility: {
                    ...settings.visibility,
                    isFullScreen: checked as boolean,
                  },
                })
              }
            />
            <Label htmlFor="is-fullscreen" className="text-sm font-normal">
              Full Screen Mode
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Theme */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Light Theme
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={settings.theme.mode}
            onValueChange={(value: 'light' | 'dark' | 'auto') =>
              updateSettings({
                theme: { ...settings.theme, mode: value },
              })
            }
          >
            <SelectTrigger id="theme-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (follow system)</SelectItem>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    </div>
  );

  if (variant === 'dialog') {
    return cardsGrid;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border-t">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full flex items-center justify-center gap-2 py-2 rounded-none"
          >
            <Settings className="h-4 w-4" />
            <span className="text-sm font-medium">Configuration</span>
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>{cardsGrid}</CollapsibleContent>
      </div>
    </Collapsible>
  );
}
